import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, Form, Table, Spinner, Badge } from 'react-bootstrap';
import { toast } from "react-hot-toast";
import { FaClipboardList, FaUserGraduate } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { FiHome, FiChevronRight } from "react-icons/fi";

import { getClassteacher } from '../../store/slices/classteacher';
import { getStudent } from '../../store/slices/student';
import { getExam } from '../../store/slices/exam';
import { getresult } from '../../store/slices/result';


const getCurrentSession = () => {
    const now = new Date();
    const month = now.getMonth() + 1;
    let startYear = now.getFullYear();
    if (month < 4) startYear -= 1;
    const endYear = (startYear + 1).toString().slice(-2);
    return `${startYear}-${endYear}`;
};

const buildSessionOptions = () => {
    const current = getCurrentSession();
    const startYear = parseInt(current.split('-')[0], 10);
    const options = [];
    for (let i = -1; i <= 1; i += 1) {
        const y = startYear + i;
        options.push(`${y}-${(y + 1).toString().slice(-2)}`);
    }
    return options;
};

export default function TeacherReportResult() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { studentlist = [] } = useSelector((state) => state.student);
    const { exam = [] } = useSelector((state) => state.exam);
    const { classteacherlist = [] } = useSelector((state) => state.classteacher);

    const [teacherId, setTeacherId] = useState('');

    const [standardId, setStandardId] = useState('');
    const [standardName, setStandardName] = useState('');
    const [sectionId, setSectionId] = useState('');
    const [sectionName, setSectionName] = useState('');

    const [session, setSession] = useState(getCurrentSession());
    const [examId, setExamId] = useState('');

    const [students, setStudents] = useState([]);
    const [studentId, setStudentId] = useState('');

    const [loadingClass, setLoadingClass] = useState(false);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [loadingResult, setLoadingResult] = useState(false);

    const [examResult, setExamResult] = useState(null);
    const [showResult, setShowResult] = useState(false);


    useEffect(() => {
        const init = async () => {
            try {
                const userStr = localStorage.getItem('user');
                if (!userStr) {
                    toast.error('User not found. Please login again.');
                    return;
                }

                const userObj = JSON.parse(userStr);
                const tId = userObj?.data?.teacherId || userObj?.teacherId;

                if (!tId) {
                    toast.error('Teacher ID not found in session.');
                    return;
                }
                setTeacherId(tId);

                setLoadingClass(true);
                const res = await dispatch(getClassteacher({ teacherId: tId })).unwrap();

                const classInfo = Array.isArray(res?.data) ? res.data[0] : res?.data;

                if (classInfo) {
                    const stdId = classInfo.standardId?._id || classInfo.standardId || '';
                    const stdName = classInfo.standardId?.name || classInfo.standardName || '';
                    const secId = classInfo.sectionId?._id || classInfo.sectionId || '';
                    const secName = classInfo.sectionId?.name || classInfo.sectionName || '';

                    setStandardId(stdId);
                    setStandardName(stdName);
                    setSectionId(secId);
                    setSectionName(secName);

                    if (stdId && secId) {
                        setLoadingStudents(true);
                        const studentRes = await dispatch(getStudent({ standardId: stdId, sectionId: secId })).unwrap();
                        setStudents(studentRes?.data || []);
                        setLoadingStudents(false);
                    }
                } else {
                    toast.error('No class assigned to this teacher.');
                }
            } catch (err) {
                toast.error(err?.message || 'Failed to load class/section details');
            } finally {
                setLoadingClass(false);
            }
        };

        init();

    }, []);


    useEffect(() => {
        const fetchExams = async () => {
            try {
                await dispatch(getExam()).unwrap();
            } catch (err) {
                toast.error(err?.message || 'Failed to load exams');
            }
        };
        fetchExams();

    }, []);


    const resetResult = () => {
        setShowResult(false);
        setExamResult(null);
    };

    const handleSubmit = async () => {
        if (!standardId || !sectionId) {
            toast.error('Standard/Section not assigned to this teacher.');
            return;
        }
        if (!session) {
            toast.error('Please select a session.');
            return;
        }
        if (!examId) {
            toast.error('Please select an exam.');
            return;
        }
        if (!studentId) {
            toast.error('Please select a student.');
            return;
        }

        try {
            setLoadingResult(true);
            resetResult();

            const res = await dispatch(getresult({

                studentId: studentId,
                examId: examId,
                session: session,

            })).unwrap();
            const resultRecord = Array.isArray(res?.data) ? res.data[0] : res?.data;

            if (!resultRecord) {
                toast.warning('No result found for the selected student & exam.');
                return;
            }

            setExamResult(resultRecord);
            setShowResult(true);
        } catch (err) {
            toast.error(err?.message || err || 'Failed to fetch result');
        } finally {
            setLoadingResult(false);
        }
    };


    const examOptions = exam?.data || exam || [];

    const totalMarks = examResult?.totalMarks || 0;
    const obtainedMarks = examResult?.obtainedMarks || 0;
    const percentage = totalMarks > 0 ? ((obtainedMarks / totalMarks) * 100).toFixed(2) : '0.00';
    const overallPass = (examResult?.subjects || []).every(
        (sub) => Number(sub.obtainedMarks) >= Number(sub.passMarks)
    );

    return (
        <>
            <div className="page-header">
                <div className="breadcrumb-section">

                    <div className="breadcrumb-left">
                        <div className="home-icon">
                            <Link to="/teacher/home">
                                <FiHome style={{ color: "white", marginTop: "-4px" }} />
                            </Link>
                        </div>

                        <FiChevronRight className="separator" />
                        <span className="active">Report Result</span>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                    </div>

                </div>
            </div>
            <div className="trc-wrapper">
                <Container fluid className="py-4">
                    <Card className="trc-filter-card mb-4 border-0 shadow-sm">
                        <Card.Body>
                            <Row className="g-3 align-items-end">
                                <Col md={3} xs={12}>
                                    <Form.Label className="trc-label">Standard</Form.Label>
                                    <Form.Select className="trc-select" value={standardId} disabled>
                                        <option value="">{loadingClass ? 'Loading...' : 'Not Assigned'}</option>
                                        {standardId && <option value={standardId}>{standardName}</option>}
                                    </Form.Select>
                                </Col>

                                <Col md={3} xs={12}>
                                    <Form.Label className="trc-label">Section</Form.Label>
                                    <Form.Select className="trc-select" value={sectionId} disabled>
                                        <option value="">{loadingClass ? 'Loading...' : 'Not Assigned'}</option>
                                        {sectionId && <option value={sectionId}>{sectionName}</option>}
                                    </Form.Select>
                                </Col>

                                <Col md={3} xs={12}>
                                    <Form.Label className="trc-label">Session</Form.Label>
                                    <Form.Select
                                        className="trc-select"
                                        value={session}
                                        onChange={(e) => {
                                            setSession(e.target.value);
                                            resetResult();
                                        }}
                                    >
                                        {buildSessionOptions().map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                <Col md={3} xs={12}>
                                    <Form.Label className="trc-label">Exam</Form.Label>
                                    <Form.Select
                                        className="trc-select"
                                        value={examId}
                                        onChange={(e) => {
                                            setExamId(e.target.value);
                                            resetResult();
                                        }}
                                    >
                                        <option value="">Select Exam</option>
                                        {examOptions.map((ex) => (
                                            <option key={ex._id} value={ex._id}>
                                                {ex.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                <Col md={3} xs={12}>
                                    <Form.Label className="trc-label">Student</Form.Label>
                                    <Form.Select
                                        className="trc-select"
                                        value={studentId}
                                        onChange={(e) => {
                                            setStudentId(e.target.value);
                                            resetResult();
                                        }}
                                        disabled={loadingStudents}
                                    >
                                        <option value="">
                                            {loadingStudents ? 'Loading...' : 'Select Student'}
                                        </option>
                                        {students.map((stu) => (
                                            <option key={stu._id} value={stu._id}>
                                                {stu.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                <Col xs={12} className="text-end mt-3">
                                    <span onClick={handleSubmit}>
                                        {loadingResult ? (
                                            <button type="button" className="btn-submit" disabled>
                                                <Spinner size="sm" animation="border" className="me-2" />
                                                Loading...
                                            </button>
                                        ) : (
                                            <button type="button" className="btn-submit">
                                                Submit
                                            </button>
                                        )}
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {showResult && examResult && (
                        <Card className="trc-table-card border-0 shadow-sm">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                    <h6 className="trc-table-title mb-0 d-flex align-items-center gap-2">
                                        <FaClipboardList /> Report Card
                                    </h6>
                                    <Badge bg={overallPass ? 'success' : 'danger'} className="trc-result-badge">
                                        {overallPass ? 'PASS' : 'FAIL'}
                                    </Badge>
                                </div>

                                <Row className="trc-meta mb-3 g-2">
                                    <Col md={3} xs={6}>
                                        <div className="trc-meta-label">Student</div>
                                        <div className="trc-meta-value d-flex align-items-center gap-1">
                                            <FaUserGraduate size={12} /> {examResult.studentId?.name}
                                        </div>
                                    </Col>
                                    <Col md={2} xs={6}>
                                        <div className="trc-meta-label">Standard</div>
                                        <div className="trc-meta-value">
                                            {examResult.standardId?.name} - {examResult.sectionId?.name}
                                        </div>
                                    </Col>
                                    <Col md={2} xs={6}>
                                        <div className="trc-meta-label">Exam</div>
                                        <div className="trc-meta-value">{examResult.examId?.name}</div>
                                    </Col>
                                    <Col md={2} xs={6}>
                                        <div className="trc-meta-label">Session</div>
                                        <div className="trc-meta-value">{examResult.session}</div>
                                    </Col>
                                </Row>

                                <div className="table-responsive">
                                    <Table className="trc-table align-middle" bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Subject</th>
                                                <th>Max Marks</th>
                                                <th>Pass Marks</th>
                                                <th>Obtained Marks</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(examResult.subjects || []).map((sub) => {
                                                const pass = Number(sub.obtainedMarks) >= Number(sub.passMarks);
                                                return (
                                                    <tr key={sub._id}>
                                                        <td>{sub.subjectId?.name}</td>
                                                        <td>{sub.maxMarks}</td>
                                                        <td>{sub.passMarks}</td>
                                                        <td>{sub.obtainedMarks}</td>
                                                        <td>
                                                            <Badge bg={pass ? 'success' : 'danger'}>
                                                                {pass ? 'Pass' : 'Fail'}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr className="trc-total-row">
                                                <td><strong>Total</strong></td>
                                                <td colSpan={2}></td>
                                                <td><strong>{obtainedMarks} / {totalMarks}</strong></td>
                                                <td><strong>{percentage}%</strong></td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </Container>

                <style>{` 
        .trc-filter-card,
        .trc-table-card {
          border-radius: 14px;
        }
        .trc-label {
          font-weight: 600;
          font-size: 0.85rem;
          color: #5e5e66;
          margin-bottom: 6px;
        }
        .trc-select,
        .trc-mark-input {
          border-radius: 8px;
          border: 1px solid #d8d3fb;
        }
        .trc-select:focus,
        .trc-mark-input:focus {
          border-color: #7c4dff;
          box-shadow: 0 0 0 0.2rem rgba(124, 77, 255, 0.15);
        }
        .trc-table-title {
          color: #4b3fbd;   
          font-weight: 700;
        }
        .trc-mark-input {
          max-width: 100px;
        }
        .trc-meta-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: #8a86a3;
          margin-bottom: 2px;
        }
        .trc-meta-value {
          font-weight: 600;
          color: #2f2b45;
        }
        .trc-result-badge {
          font-size: 0.85rem;
          padding: 6px 14px;
        }
        .trc-total-row td {
          background-color: #f7f5ff;
        }
        @media (max-width: 768px) {
          .trc-table {
            font-size: 0.85rem;
          }
          .trc-mark-input {
            max-width: 70px;
          }
        }
      `}</style>
            </div>
        </>
    );
}